import { CallbackAction } from '@fanduel/geolocation-sdk';

import {
  AW_TO_SDK_MAP,
  type AwGeolocationActionType,
} from '@/features/geolocation/types';

/*
 * Normalizes aw-geolocation action types to geolocation-sdk action types for consistent logging.
 * This handles legacy action types from iOS/Android native apps.
 */
export const normalizeActionType = (
  actionType: string,
): CallbackAction['type'] => {
  if (actionType in AW_TO_SDK_MAP) {
    return AW_TO_SDK_MAP[actionType as AwGeolocationActionType];
  }

  return actionType as CallbackAction['type'];
};

/**
 * Type guard to check if a value is a valid CallbackAction payload structure.
 * A valid payload must be an object with a 'type' property that is a string.
 *
 * @param payload - The value to validate
 * @returns True if the payload has a valid CallbackAction structure
 */
export const isValidCallbackActionPayload = (
  payload: unknown,
): payload is { type: string; [key: string]: unknown } =>
  typeof payload === 'object' &&
  payload !== null &&
  'type' in payload &&
  typeof (payload as { type: unknown }).type === 'string';

/**
 * Validates and normalizes a geolocation payload from a wrapper message.
 * If the payload is valid, it normalizes the action type and returns the normalized CallbackAction.
 * If invalid, returns null.
 *
 * @param payload - The payload to validate and normalize
 * @returns Normalized CallbackAction if valid, null otherwise
 */
export const validateAndNormalizePayload = (
  payload: unknown,
): CallbackAction | null => {
  if (!isValidCallbackActionPayload(payload)) {
    return null;
  }

  const { type, ...rest } = payload;

  return {
    ...rest,
    type: normalizeActionType(type),
  } as CallbackAction;
};
